import Vue from 'vue';
import Vuex from 'vuex';
import _ from 'lodash';
import { GOAL_HEADER_BUS_ACTIONS, goalHeaderBus } from './goalHeaderBus.js';
import { TASK_ACTION_BUS_ACTIONS, taskActionsBus } from '../../taskActionsBus';
import config from '../../config';
import { customTermFilter } from '~/vue/filters/CustomTerm';
import GoalResource from '~/angular/app/projects/project_resource';
import GoalTaskResource from '~/angular/app/projects/project_task_resource';
import { CurrentUserClass } from '~/angular/app/shared/CurrentUser';
import { simplePopupFactory } from '~/angular/app/shared/simple_popups/simplePopupFactory';
import FinishGoalOptionToHidePopup from '~/angular/app/goals/finish_goal/FinishGoalOptionToHidePopup.vue';
import FinishGoalPillarsOfSupportPopup from '~/angular/app/goals/finish_goal/FinishGoalPillarsOfSupportPopup.vue';
import FinishGoalLifeImpactPopup from '~/angular/app/goals/finish_goal/FinishGoalLifeImpactPopup.vue';

Vue.use(Vuex);

export const COMPLETION_FILTER_OPTIONS = {
    TO_DO: 0,
    COMPLETED: 1,
    ALL: 2,
};

function isEditModePossible(isAdmin, tasks, currentUserId) {
    if (isAdmin) {
        return true;
    }
    for (let task of tasks) {
        if (task.created_by.id === currentUserId) {
            return true;
        }
    }
    return false;
}

function getTasksByCompletionStateCounters(flattenedTasks) {
    const res = {
        all: {
            total: 0,
            completed: 0,
        },
        assignedToMeOrToNobody: {
            total: 0,
            completed: 0,
        },
        assignedToMe: {
            total: 0,
            completed: 0,
        },
    };
    const myId = new CurrentUserClass().id;
    for (let i = 0; i < flattenedTasks.length; i++) {
        let task = flattenedTasks[i];
        const isAssignedToMe = task.assignee?.id === myId;
        const isAssignedToNobody = !task.assignee;
        const isCompleted = task.completed;

        res.all.total += 1;
        if (isAssignedToNobody || isAssignedToMe) {
            res.assignedToMeOrToNobody.total += 1;
            if (isAssignedToMe) {
                res.assignedToMe.total += 1;
            }
        }
        if (isCompleted) {
            res.all.completed += 1;
            if (isAssignedToNobody || isAssignedToMe) {
                res.assignedToMeOrToNobody.completed += 1;
                if (isAssignedToMe) {
                    res.assignedToMe.completed += 1;
                }
            }
        }
    }
    return res;
}

const store = {
    namespaced: true,
    state() {
        return {
            tasks: [],
            members: [],
            goal: {},
            isMember: false,
            isAdmin: false,
            editMode: false,
            joinRequestPending: false,
            currentUser: {},
            tagsInFilter: [],
            assigneesInFilter: [],
            activeUrl: undefined,
            currentlyDraggedTask: undefined,
            currentlyUnderDragTask: undefined,
            showOtherUsersTasks: false,
            activeCompletionFilter: COMPLETION_FILTER_OPTIONS.TO_DO,
        };
    },
    mutations: {
        setActiveCompletionFilter(state, val) {
            state.activeCompletionFilter = val;
        },
        set_show_other_users_tasks(state, val) {
            state.showOtherUsersTasks = val;
        },
        set_join_request_pending(state, val) {
            state.joinRequestPending = val;
        },
        set_currently_dragged_task(state, task) {
            state.currentlyDraggedTask = task;
        },
        reset_currently_dragged_task(state) {
            state.currentlyDraggedTask = undefined;
        },
        set_currently_under_drag_task(state, task) {
            state.currentlyUnderDragTask = task;
        },
        reset_currently_under_drag_task(state) {
            state.currentlyUnderDragTask = undefined;
        },
        drop_task(state, {
            taskToMove,
            targetTask
        }) {
            const taskToMoveIndex = state.tasks.indexOf(taskToMove);
            const targetTaskIndex = state.tasks.indexOf(targetTask);
            const correction = taskToMoveIndex > targetTaskIndex ? 0 : 1;
            state.tasks.splice(taskToMoveIndex, 1);
            const newTargetTaskIndex = state.tasks.indexOf(targetTask);
            state.tasks.splice(newTargetTaskIndex + correction, 0, taskToMove);
            state.currentlyDraggedTask = undefined;
            state.currentlyUnderDragTask = undefined;
        },
        set_active_url(state, url) {
            state.activeUrl = url;
        },
        delete_task(state, task) {
            state.tasks.splice(state.tasks.indexOf(task), 1);
        },
        add_task(state, task) {
            state.tasks.unshift(task);
        },
        update_task(state, updatedTask) {
            const index = _.findIndex(state.tasks, t => t.id === updatedTask.id);
            Vue.set(state.tasks, index, updatedTask);
        },
        toggle_filter_by_tag(state, tag) {
            if (state.tagsInFilter.indexOf(tag) === -1) {
                state.tagsInFilter.push(tag);
            } else {
                state.tagsInFilter.splice(state.tagsInFilter.indexOf(tag), 1);
            }
            taskActionsBus.$emit(TASK_ACTION_BUS_ACTIONS.SET_TAGS_IN_FILTER, state.tagsInFilter);
        },
        set_tags_in_filter(state, value) {
            state.tagsInFilter = value;
        },
        set_current_user(state, value) {
            state.currentUser = value;
        },
        set_members(store, members) {
            store.members = [];
            store.members = members.map(m => m);
        },
        set_edit_mode(state, {
            value,
            emitEvent
        }) {
            state.editMode = value;
            if (emitEvent) {
                goalHeaderBus.$emit(GOAL_HEADER_BUS_ACTIONS.TOGGLE_EDIT_MODE, value);
            }
        },
        set_is_admin(state, isAdmin) {
            state.isAdmin = isAdmin;
            if (!isAdmin) {
                state.editMode = false;
            }
        },
        update_members_admin_status(state, {
            newAdmins,
            noMoreAdmins
        }) {
            for (let i = 0; i < state.members.length; i++) {
                let member = state.members[i];
                if (newAdmins && newAdmins.indexOf(member.id) !== -1) {
                    member.is_admin = true;
                }
                if (noMoreAdmins && noMoreAdmins.indexOf(member.id) !== -1) {
                    member.is_admin = false;
                }
            }
        },
        delete_member(state, {
            member,
            mode
        }) {
            state.members.splice(state.members.indexOf(member), 1);
            if (mode === 'delete') {
                state.tasks = state.tasks.filter(t => (!t.assignee || t.assignee.id !== member.id));
            }
            if (mode === 'reset') {
                for (let task of state.tasks) {
                    if (task.assignee && task.assignee.id === member.id) {
                        task.assignee = undefined;
                    }
                }
            }
        },
        add_member(state, memberData) {
            state.members.push(memberData);
        },
        set_is_member(state, isMember) {
            state.isMember = isMember;
        },
        set_tasks(state, tasks) {
            state.tasks = tasks;
        },
        set_goal(state, goal) {
            state.goal = goal;
        },
        update_task_from_server_data(state, task) {
            let index_in_list = _.findIndex(state.tasks, t => t.id === task.id);
            Vue.set(state.tasks, index_in_list, task);
        },
        empty() {

        },
        set_assignees_in_filter(state, value) {
            state.assigneesInFilter = value['id_list'];
            let raise_event = true;
            if (value['raise_event'] !== undefined) {
                raise_event = value['raise_event'];
            }
            if (raise_event) {
                taskActionsBus.$emit(TASK_ACTION_BUS_ACTIONS.SET_ASSIGNEES_IN_FILTER, value['id_list']);
            }
        }
    },
    actions: {
        create_task({
            state,
            commit
        }, taskData) {
            Vue.loadingSpinner.show();
            GoalTaskResource().create_task(state.goal.id, taskData)
                .then(resp => {
                    commit('add_task', resp.data.created_task);
                    if (resp.data.added_member) {
                        commit('add_member', resp.data.added_member);
                    }
                    Vue.notifications.success(`${customTermFilter('Task')} created`);
                }, err => {
                    Vue.notifications.error(err || 'Error');
                })
                .finally(Vue.loadingSpinner.hide);
        },
        drop_task({
            state,
            commit
        }) {
            const taskToMove = state.currentlyDraggedTask;
            const targetTask = state.currentlyUnderDragTask;
            commit('drop_task', {
                taskToMove,
                targetTask
            });
            const orderedIdList = state.tasks.map(t => t.id);
            Vue.loadingSpinner.show();
            GoalResource().reorder_tasks(state.goal.id, orderedIdList)
                .then(resp => {
                    Vue.notifications.success('Reordered');
                }, err => {
                    Vue.notifications.success(err || 'Error');
                })
                .finally(() => {
                    Vue.loadingSpinner.hide();
                });
        },
        make_me_admin({
            state,
            commit,
            dispatch
        }) {
            Vue.loadingSpinner.show();
            GoalResource().make_me_admin(state.goal.id)
                .then(resp => {
                    commit('set_is_admin', true);
                    commit('update_members_admin_status', { newAdmins: [new CurrentUserClass().id] });
                    dispatch('load_tasks');
                    Vue.notifications.success(`You are admin of this ${customTermFilter('Goal')} now.`);
                }, err => Vue.notifications.error(err || 'Error'))
                .finally(() => Vue.loadingSpinner.hide());
        },
        load_tasks({
            state,
            commit
        }) {
            Vue.loadingSpinner.show();
            return GoalTaskResource().get_goal_actions(state.goal.id)
                .then(resp => {
                    commit('set_tasks', resp.data);
                    Vue.loadingSpinner.hide();
                }, err => {
                    Vue.loadingSpinner.hide();
                    Vue.notifications.error(err || 'Error');
                });
        },
        async finishGoalFlow({
            state,
            dispatch
        }) {
            try {
                // Initialize state variables
                const flowState = {
                    step: 1,
                    lifeImpactComment: '',
                    selectedPillars: [],
                    hideGoal: true
                };

                // State machine to handle steps and back navigation
                while (true) {
                    switch (flowState.step) {
                        case 1: // Life impact comment
                            try {
                                flowState.lifeImpactComment = await dispatch('showFinishGoalStep1', flowState.lifeImpactComment);
                                flowState.step = 2; // Move to next step
                            } catch (error) {
                                if (error === 'back') {
                                    // No previous step to go back to
                                    continue;
                                }
                                throw error;
                            }
                            break;

                        case 2: // Pillars of support
                            try {
                                flowState.selectedPillars = await dispatch('showFinishGoalStep2', flowState.selectedPillars);
                                flowState.step = 3; // Move to next step
                            } catch (error) {
                                if (error === 'back') {
                                    flowState.step = 1; // Go back to previous step
                                    continue;
                                }
                                throw error;
                            }
                            break;

                        case 3: // Hide goal option
                            try {
                                flowState.hideGoal = await dispatch('showFinishGoalStep3', flowState.hideGoal);
                                // Process the completed flow
                                await dispatch('processFinishGoal', {
                                    lifeImpactComment: flowState.lifeImpactComment,
                                    selectedPillars: flowState.selectedPillars,
                                    hideGoal: flowState.hideGoal
                                });
                                return; // Exit the state machine
                            } catch (error) {
                                if (error === 'back') {
                                    flowState.step = 2; // Go back to previous step
                                    continue;
                                }
                                throw error;
                            }
                            break;
                    }
                }
            } catch (error) {
                // Handle any unexpected errors
                if (error !== 'back' && error !== 'close') {
                    Vue.notifications.error('Failed to finish goal: ' + error);
                }
            }
        },

        async showFinishGoalStep1(context, initialText = '') {
            const {
                popupComponent,
                vm
            } = simplePopupFactory.mount_vue_popup(FinishGoalLifeImpactPopup, { initialText });
            const comment = await popupComponent.show();
            vm?.$destroy();
            return comment;
        },

        async showFinishGoalStep2(context, initialSelection = []) {
            const {
                popupComponent,
                vm
            } = simplePopupFactory.mount_vue_popup(FinishGoalPillarsOfSupportPopup, { initialSelectedPillars: initialSelection });
            const selectedPillars = await popupComponent.show();
            vm?.$destroy();
            return selectedPillars;
        },

        async showFinishGoalStep3(context, initialHideGoal = true) {
            const {
                popupComponent,
                vm
            } = simplePopupFactory.mount_vue_popup(FinishGoalOptionToHidePopup, { initialHideGoal });
            const hideGoal = await popupComponent.show();
            vm?.$destroy();
            return hideGoal;
        },

        async processFinishGoal({ state, commit }, {
            lifeImpactComment,
            selectedPillars,
            hideGoal
        }) {
            // This would be the equivalent of the finishGoal method from the component
            Vue.loadingSpinner.show();
            try {
                // Call API to finish the goal with the collected information
                const data = {
                    life_impact_comment: lifeImpactComment,
                    pillars_of_support: selectedPillars,
                    hide_goal: hideGoal
                };
                const resp = await GoalResource().finish_goal(state.goal.id, data);
                commit('set_goal', resp.data);
                Vue.notifications.success(`${customTermFilter('Goal')} finished successfully!`);
            } catch (error) {
                Vue.notifications.error(error || `Error finishing ${customTermFilter('Goal')}`);
            } finally {
                Vue.loadingSpinner.hide();
            }
        },

        async processUnfinishGoal({ state, commit }, ) {
            // This would be the equivalent of the finishGoal method from the component
            Vue.loadingSpinner.show();
            try {
                const resp = await GoalResource().unfinish_goal(state.goal.id);
                commit('set_goal', resp.data);
                Vue.notifications.success(`${customTermFilter('Goal')} made active again!`);
            } catch (error) {
                Vue.notifications.error(error || `Error making ${customTermFilter('Goal')} active`);
            } finally {
                Vue.loadingSpinner.hide();
            }
        }
    },
    getters: {
        flattenedTasks(state, getters) {
            let taskList = [];
            if (state.tasks && state.tasks.length) {
                for (let i = 0; i < state.tasks.length; i++) {
                    let task = state.tasks[i];
                    taskList.push(task);
                    if (task.children) {
                        for (let i = 0; i < task.children.length; i++) {
                            const child = task.children[i];
                            taskList.push(child);
                        }
                    }
                }
            }

            return taskList;
        },
        activeCompletionFilter(state, getters) {
            return state.activeCompletionFilter;
        },
        tasksCompletionStatsTotalCounter(state, getters) {
            const tasksCompletionStatsCounters = getTasksByCompletionStateCounters(getters.flattenedTasks);
            const tasksAreHidden = state.goal.hide_tasks;
            const viewedByAdmin = state.isAdmin;
            let relevantCounters = tasksCompletionStatsCounters.all;
            if (!viewedByAdmin) {
                if (tasksAreHidden) {
                    relevantCounters = tasksCompletionStatsCounters.assignedToMe;
                } else {
                    relevantCounters = tasksCompletionStatsCounters.all; //assignedToMeOrToNobody?
                }
            }
            return relevantCounters;
        },
        completedPercentage(state, getters) {
            if (state.tasks.length === 0) {
                return 0;
            }
            return Math.round(100 * (getters.tasksCompletionStatsTotalCounter.completed / getters.tasksCompletionStatsTotalCounter.total));
        },
        tagsInProject(state, getters) {
            const tagsDict = {};
            for (let task of getters.flattenedTasks) {
                for (let tag of task.tags) {
                    if (tagsDict[tag]) {
                        tagsDict[tag]++;
                    } else {
                        tagsDict[tag] = 1;
                    }
                }
            }
            return _.orderBy(_.keys(tagsDict), key => tagsDict[key], 'desc');
        },
        isEditModePossible(state, getters) {
            return isEditModePossible(state.isAdmin, state.tasks, state.currentUser.id);
        },
        iCanMakeMeAdmin(state, getters) {
            return state.isMember && !state.isAdmin && new CurrentUserClass().is_guardian_of(state.goal.owner.id);
        },
        filterIsActive(state, getters) {
            return !!(state.assigneesInFilter.length || state.tagsInFilter.length);
        },
        showOtherUsersTasks(state, getters) {
            return state.showOtherUsersTasks;
        },
        visibleToMeTasks(state, getters) {
            const flattenTasks = getters.filterIsActive || (!state.showOtherUsersTasks && !state.isAdmin) || [COMPLETION_FILTER_OPTIONS.COMPLETED, COMPLETION_FILTER_OPTIONS.TO_DO].includes(state.activeCompletionFilter);

            let taskList;
            if (flattenTasks) {
                taskList = getters.flattenedTasks;
            } else {
                taskList = state.tasks;
            }

            const isTaskFilteredByTags = (task) => {
                return state.tagsInFilter?.length === 0 || _.intersection(task.tags, state.tagsInFilter).length > 0;
            }

            let taskIsVisible = function (task) {
                if (state.assigneesInFilter.length > 0) {
                    if (task.assignee) {
                        return state.assigneesInFilter.indexOf(task.assignee.id) !== -1;
                    } else {
                        return state.assigneesInFilter.indexOf(config.EMPTY_ASSIGNEE_ID) !== -1;
                    }
                }
                if (state.isAdmin) {
                    return isTaskFilteredByTags(task);
                }
                if (state.showOtherUsersTasks) {
                    return isTaskFilteredByTags(task);
                }
                if (!task.assignee) {
                    return isTaskFilteredByTags(task);
                }
                if (task.created_by.id === state.currentUser.id) {
                    return isTaskFilteredByTags(task);
                }
                if (task.assignee.id === state.currentUser.id) {
                    return isTaskFilteredByTags(task);
                }
            };
            return taskList.filter(t => taskIsVisible(t));
        },
        availableAssignees(state, getters) {
            const assignees = [{
                'name': config.EMPTY_ASSIGNEE_NAME,
                'id': config.EMPTY_ASSIGNEE_ID,
                'avatar': config.EMPTY_ASSIGNEE_AVATAR
            }];
            for (let _task of getters.flattenedTasks) {
                if (_task.assignee && !assignees.filter(a => a.id === _task.assignee.id).length) {
                    assignees.push(_task.assignee);
                }
            }
            return assignees;
        }
    },
    modules: {
        // timeline
    }
};

export default store;
